import ApiService from '../_shared/api/apiService';
import { KeyValuePair, TradeListRequest } from '../_shared/types';
import Singleton from '../Singleton';
import { TradeState } from '../_shared/types/trade';

export default class TradeListService extends Singleton<TradeListService> {
  /**
   * Public Methods
   */
  public getTradeList = async ({
    context,
    pagination,
    tradeState,
    clientSupportMode,
    isTradeFilterEnabled,
    isRetailTradesIncluded
  }: TradeListRequest) => {
    const client = await ApiService();

    if (context && pagination && tradeState) {
      try {
        const tradeListResponse = await client.apis.trade.listTrades({
          request: {
            request: {
              networkId: null,
              pagination,
              tradeState,
              context,
              clientSupportMode,
              isTradeFilterEnabled,
              isRetailTradesIncluded
            }
          }
        });
        return tradeListResponse?.body;
      } catch (e) {
        // TODO handle exception
        return e;
      }
    }
    return null;
  };

  // Returns the details of a list
  public getList = async tradeListRequest => {
    const client = await ApiService();

    const {
      listId,
      offset,
      max,
      pagination,
      tradeState,
      networkId
    } = tradeListRequest;

    const request = {
      request: {
        pagination: {
          pagination: { offset, max },
          sortMethod: pagination.sortMethod
        },
        tradeState,
        networkId
      }
    };

    try {
      if (!listId) return false;

      const listResponse = await client.apis.list.getList({
        id: listId,
        request
      });

      return listResponse?.body;
    } catch (e) {
      throw new Error(e);
    }
  };

  // TODO: getSavedList is not complete
  public getSavedList = async ({ context, pagination, tradeState }) => {
    const client = await ApiService();

    if (context && pagination && tradeState) {
      try {
        const tradeListResponse = await client.apis.list.getList({
          id: 'watchlist',
          request: {
            request: {
              networkId: null,
              pagination,
              tradeState,
              context
            }
          }
        });

        return tradeListResponse?.body;
      } catch (e) {
        // TODO handle exception
        return e;
      }
    }
    return null;
  };

  public createList = async ({ title, criteria, enableNotification }) => {
    const client = await ApiService();

    try {
      return await client.apis.list.createList({
        request: {
          request: {
            title,
            criteria,
            enableNotification
          }
        }
      });
    } catch (e) {
      return new Error(e);
    }
  };

  public updateList = async ({ id, criteria, enableNotification }) => {
    const client = await ApiService();

    try {
      return await client.apis.list.updateList({
        id,
        request: {
          request: {
            criteria,
            enableNotification
          }
        }
      });
    } catch (e) {
      return new Error(e);
    }
  };

  public toggleNotifications = async ({ enableNotification, id }, criteria) => {
    try {
      const toggleNotification = await this.updateList({
        id,
        criteria,
        enableNotification: !enableNotification
      });

      return {
        res: toggleNotification,
        enableNotification: !enableNotification
      };
    } catch (e) {
      return e;
    }
  };

  // Get trade metadata
  public getMetaData = async () => {
    const client = await ApiService();

    try {
      const tradeCreateMetaDataResponse = await client.apis.trade.createMetadata();

      return tradeCreateMetaDataResponse?.body;
    } catch (e) {
      throw new Error(e);
    }
  };

  // Gets the preview of a list
  public getPreview = async ({
    criteria,
    networkId = null,
    pagination,
    tradeState,
    offset,
    max
  }) => {
    const client = await ApiService();

    try {
      const tradeListResponse = await client.apis.list.previewList({
        request: {
          request: {
            criteria,
            networkId,
            pagination: {
              pagination: {
                offset,
                max
              },
              sortMethod: pagination.sortMethod
            },
            tradeState
          }
        }
      });

      return tradeListResponse?.body;
    } catch (e) {
      throw new Error(e);
    }
  };

  public impoundTrade = async tradeId => {
    const client = await ApiService();

    try {
      return await client.apis.list.addToList({
        id: 'impound',
        request: {
          id: 'impound',
          request: { tradeId }
        }
      });
    } catch (e) {
      return e;
    }
  };

  public unimpoundTrade = async tradeId => {
    const client = await ApiService();

    try {
      return await client.apis.list.removeFromList({
        id: 'impound',
        request: {
          id: 'impound',
          request: { tradeId }
        }
      });
    } catch (e) {
      return e;
    }
  };

  public moveToTradeState = async (
    tradeId: number,
    currentState: TradeState,
    newState: TradeState,
    stateChangeProps: KeyValuePair[] = []
  ) => {
    const client = await ApiService();
    try {
      const response = await client.apis.trade
        .moveTrade({
          id: tradeId,
          request: {
            id: tradeId,
            request: {
              currentState,
              desiredState: newState,
              stateChangeProperties: stateChangeProps
            }
          }
        })
        .catch(e => ({
          isError: true,
          errorMessage: e.response?.body?.message,
          errorCode: e.response?.body?.code
        }));

      return response;
    } catch (e) {
      return null;
    }
  };

  public launchToNetwork = async (tradeId, networkId, startingBidAmount) => {
    const client = await ApiService();
    try {
      return await client.apis.trade.launchInternal({
        id: tradeId,
        request: {
          id: tradeId,
          request: {
            networkId,
            startingBidAmount
          }
        }
      });
    } catch (e) {
      return null;
    }
  };

  public moveToClosing = async (
    tradeId: number,
    currentState: TradeState,
    stateChangeProperties: KeyValuePair[]
  ) => {
    return this.moveToTradeState(
      tradeId,
      currentState,
      TradeState.closing,
      stateChangeProperties
    );
  };
}
